import React, { FC, useLayoutEffect, useMemo, useState } from 'react';
import { ItvRowCollection } from '../../Itv/ItvRowCollection';
import { useTranslations } from '../../../hooks/useTranslationsHelper';
import { Paragraph } from '../../shared/Paragraph';
import { Spinner } from '../../shared/Spinner';
import './ItvRecommendedGames.scss';
import { GameItemResponse } from '../../Games/games.model';
import { useFetchGames } from '../../../hooks/useFetchGames';
import { GAME_CATEGORIES, locale } from '../../../consts';
import { transformCategoriesGameData } from '../../../utils/games/games.utils';
import { getGameCarouselConfig } from '../../Games/GameInfo/GamesCarouselConfig';
import { buildCollectionTiles, CollectionGames } from '../ItvRowCollection/utils/collectionTiles';
import { ItvCollectionType } from '../types';
import { ItvViewAllGrid } from '../ItvViewAll/ItvViewAllGrid';
import { useDeviceInfo } from '../../../hooks/useDeviceInfo';

interface ItvRecommendedGamesProps {
  title?: string;
  type?: ItvCollectionType;
  eligibleGames?: CollectionGames;
  fluidHeight?: boolean;
  disableMenu?: boolean;
}

export const ItvRecommendedGames: FC<ItvRecommendedGamesProps> = ({
  type = ItvCollectionType.GAMES,
  eligibleGames = null,
  title,
  fluidHeight = false,
  disableMenu = false,
}): JSX.Element => {
  const { t } = useTranslations();
  const { slidesCount } = getGameCarouselConfig(t);
  const { isMobileDevice } = useDeviceInfo();

  const isEligibleGames = eligibleGames?.length;
  const [collectionGames, setCollectionGames] = useState<CollectionGames>(eligibleGames);
  const [gamesTiles, setGamesTiles] = useState<(JSX.Element | null)[]>([]);
  const [showViewAll, setShowViewAll] = useState<boolean>(false);

  const layoutType = useMemo(() => (isMobileDevice ? 'row-4' : 'row-5'), [isMobileDevice]);

  const { isFetchedGamesByCategoryDataLoading, fetchGamesByCategory } = useFetchGames({
    language: locale,
    count: slidesCount,
    category: GAME_CATEGORIES.RECOMMENDED,
    onGamesGetByCategoryComplete: (data) => {
      const transformedData = transformCategoriesGameData(data.categories);

      if (transformedData.length) {
        // Convert Game[] to CollectionGames format for buildCollectionTiles
        const collectionGamesFormat: CollectionGames = transformedData.map((game) => ({
          game_id: {
            id: game.gameId,
            display_name: game.displayName,
            game_image: game.gameImage,
            game_image_box: game.gameImageBox,
            translations: [{ slug: game.friendlyUrlName, display_name: game.displayName }],
          } as GameItemResponse,
        }));

        setCollectionGames(collectionGamesFormat);
      }
    },
  });

  useLayoutEffect(() => {
    // Fetch recommended games or set it from eligible data
    !eligibleGames &&
      (async (): Promise<void> => {
        await fetchGamesByCategory();
      })();
  }, [isEligibleGames, eligibleGames]);

  useLayoutEffect(() => {
    if (collectionGames) {
      const tiles = buildCollectionTiles({
        collectionId: 'recommended-games',
        collectionType: ItvCollectionType.GAMES,
        layoutType,
        games: collectionGames,
        fallbackImages: ['/images/fallback-game.png'], // You might want to add actual fallback images
        stubImageClassName: 'itv-recommended-games__demo-image',
        disableMenu,
        isMobile: isMobileDevice,
      });

      setGamesTiles(tiles);
    }
  }, [collectionGames, layoutType, disableMenu, isMobileDevice]);

  const hasGames = gamesTiles.length > 0;

  return (
    <div className="itv-recommended-games">
      {isFetchedGamesByCategoryDataLoading ? (
        <Spinner className="itv-recommended-games__spiner" />
      ) : hasGames ? (
        showViewAll ? (
          <ItvViewAllGrid
            title={title}
            tiles={gamesTiles}
            backBtnTitle={t('game.info.go-back-btn')}
            onBack={(): void => setShowViewAll(false)}
          />
        ) : (
          <ItvRowCollection
            fluidHeight={fluidHeight}
            layout_type={layoutType}
            type={type}
            title={title}
            showViewAll
            onViewAll={(): void => setShowViewAll(true)}
          >
            {gamesTiles}
          </ItvRowCollection>
        )
      ) : (
        <Paragraph className="itv-recommended-games__empty" noMargin>
          {t('promotions.slides.empty-title')}
        </Paragraph>
      )}
    </div>
  );
};
